/* ====================================================================
 *  imageSizes — ЄДИНЕ джерело правди для ширин зображень (п.9в).
 *
 *  ЧОМУ ОКРЕМИЙ МОДУЛЬ. На Vercel оптимізатор приймає лише ті ширини,
 *  що оголошені в `image.screens`: @nuxt/image кладе `Object.values`
 *  цього об'єкта в `images.sizes` згенерованого
 *  `.vercel/output/config.json`, і будь-яка інша ширина повертає 400.
 *  Заміряно на проді до фіксу: 640/768/1024/1280/1536 → 200,
 *  а 480/750/1440/1920/2560 → 400 — при тому, що останні були
 *  оголошені у `vercel.json`. Блок `images` у `vercel.json` платформа
 *  ІГНОРУЄ, коли є build output config (`headers`/`redirects` там
 *  працюють — це різні речі, не плутати).
 *
 *  Перевірити локально: `VERCEL=1 npm run build`, далі подивитись
 *  `images.sizes` у `.vercel/output/config.json`. Без VERCEL=1
 *  провайдер — `ipx`, і секції `images` у конфізі не буде взагалі.
 *
 *  Тому і `nuxt.config.ts`, і всі місця виклику беруть ширини звідси,
 *  а `test/utils/imageSizes.spec.ts` стежить, щоб жодна використана
 *  ширина не випала з IMAGE_SCREENS.
 * ==================================================================== */

/**
 * Дозволені ширини. Ключі — довільні мітки (значущі лише ЗНАЧЕННЯ,
 * саме вони йдуть в allowlist Vercel), але sm…2xl навмисно збігаються
 * з брейкпоінтами Tailwind: `NuxtImg` вміє синтаксис `sizes="sm:100vw"`
 * і читає імена звідси. xs/xsm додані під картки каталогу.
 */
export const IMAGE_SCREENS: Record<string, number> = {
  xs: 320,
  xsm: 480,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  "2xl": 1536,
};

/** Плаский відсортований список — те, що реально стане allowlist'ом. */
export const ALLOWED_IMAGE_WIDTHS: number[] = [
  ...new Set(Object.values(IMAGE_SCREENS)),
].sort((a, b) => a - b);

/**
 * Обкладинка картки товару.
 * Звірено з реальною сіткою ProductGrid (`grid-cols-2 / md:3 / lg:4`,
 * контейнер до 1536, px-4/px-8, gap-3/gap-4):
 *   – мобільний 390px → картка ≈173 CSS px → DPR2 ≈346 → 480;
 *   – десктоп 1536px → картка ≈356 CSS px → DPR2 ≈712 → 768.
 * 320 лишається для DPR1-мобільних. Вище 768 сенсу немає: більшої
 * картки сітка не дає.
 */
export const CARD_WIDTHS: number[] = [320, 480, 768];

/**
 * Головний кадр галереї товару (п.9г). Сітка сторінки —
 * `grid-cols-1 lg:grid-cols-2`, контейнер до 1536 із px-4/px-8:
 *   – мобільний 390px → кадр ≈358 CSS px → DPR2 ≈716 → 768;
 *   – десктоп 1536px → колонка ≈736 CSS px → DPR2 ≈1472 → 1536.
 * 480 лишається для DPR1-мобільних, 1024 — для планшета й DPR1-десктопа.
 */
export const GALLERY_WIDTHS: number[] = [480, 768, 1024, 1536];

/**
 * `sizes` для головного кадру. Живе поруч із ширинами, бо мусить бути
 * ОДНАКОВИМ у `<img>` і в `<link rel=preload imagesizes>`: розбіжність
 * тут — це другий раунд завантаження того самого кадру.
 */
export const GALLERY_SIZES = "(min-width: 1024px) 50vw, 100vw";

/**
 * Прев'ю під головним кадром: `grid-cols-5`, тобто на десктопі
 * ≈133 CSS px, на мобільному ≈65. Навіть DPR2-десктоп — це 266,
 * і 320 (найменша дозволена ширина) покриває всі випадки з запасом.
 * Один розмір, а не список: другого кандидата тут просто нема з чого
 * робити — усе, що більше, було б чистим перевитратом.
 */
export const THUMB_WIDTHS: number[] = [320];

/** Hero, мобільна орієнтація (`<source media="(max-width: 767px)">`). */
export const HERO_MOBILE_WIDTHS: number[] = [480, 640, 768];

/** Hero, десктопна орієнтація (`<source media="(min-width: 768px)">`). */
export const HERO_DESKTOP_WIDTHS: number[] = [1024, 1280, 1536];

/**
 * Якість головного кадру галереї — максимальна, і це свідоме рішення.
 *
 * Чому не спільні 75. Вихідні фото товарів самі по собі невеликі:
 * заміряно 1023×1537 і 1526×2035, тобто на ретині кадр показується
 * майже піксель-у-піксель, і будь-яке зайве стиснення видно одразу —
 * клієнт, який наближає тканину, бачить кашу. Головна картинка
 * сторінки товару — це те, за чим приймають рішення про купівлю.
 *
 * Ціна, заміряна на проді (avif, w=1536, той самий кадр):
 *   q75 → 32 250 b, q90 → 44 527 b, q100 → 53 367 b
 * а для більшого джерела (1526×2035): 17 987 / 27 976 / 34 774 b.
 * Навіть на 100 кадр легший за сирий оригінал (82 877 b), який
 * сторінка віддавала до п.9г.
 *
 * Прев'ю лишаються на загальній якості: у боксі 120 px різниці не
 * видно, а кадрів там до п'яти.
 */
export const GALLERY_QUALITY = 100;

/**
 * Якість для всіх викликів.
 * ВАЖЛИВО: глобальний `image.quality` у nuxt.config тут НЕ допоможе —
 * @nuxt/image підставляє його лише всередині <NuxtImg>/<NuxtPicture>
 * (runtime/utils/props.js), а голий `useImage()` його не читає і
 * дефолтить на q=100. Тому передаємо явно в кожен виклик.
 */
export const IMAGE_QUALITY = 75;
